import { supabase } from './supabase'
import type { SpaceKind } from './categories'
import { todayIso } from './dates'

/**
 * Obligations, and the events that settle them.
 *
 * There is no status column anywhere (rule 4). What an instalment currently
 * *is* comes from the `instalment_state` view, which computes it from the
 * append-only payment_events ledger. Marking something paid therefore never
 * updates a row — it appends a fact.
 */

export type PaymentState = 'to_pay' | 'planned' | 'paid' | 'needs_attention' | 'cancelled'

export interface Payable {
  instalmentId: string
  obligationId: string
  spaceId: string
  description: string
  supplierName: string | null
  partyId: string | null
  amountCents: number
  paidCents: number
  dueDate: string
  plannedDate: string | null
  state: PaymentState
  iban: string | null
  paymentReference: string | null
  verified: boolean
  sourceDocumentId: string | null
  category: string | null
  /** "Zaak" or "Huis" — resolved from supplier history, never asked. */
  spaceName: string | null
  spaceKind: SpaceKind | null
  /** How many previous invoices used this same account. */
  ibanSightings: number
  ibanChanged: boolean
  disputed: boolean
  cancelled: boolean
  /**
   * A placeholder a recurring rule put in the calendar, not a bill anyone
   * sent. §10: Estimated and Committed money are never mixed, and the
   * database refuses to pay one of these.
   */
  /** A hard deadline has real consequences; a soft one is what may move. */
  isHard: boolean
  isEstimate: boolean
  recurringRuleId: string | null
  /** "€780. Meestal €480–€530." — set when a bill drifts from its own history. */
  notes: string | null
}

export async function loadPayables(): Promise<Payable[]> {
  const { data: states, error } = await supabase
    .from('instalment_state')
    .select('instalment_id, obligation_id, space_id, amount_cents, paid_cents, credited_cents, due_date, planned_date, state, disputed, cancelled')
    .order('due_date', { ascending: true })
  if (error) throw error
  if (!states || states.length === 0) return []

  const obligationIds = [...new Set(states.map((s) => s.obligation_id as string))]

  const { data: obligations } = await supabase
    .from('obligations')
    .select('id, description, category, iban, payment_reference, verified_at, party_id, source_document_id, notes, is_hard')
    .in('id', obligationIds)
    .is('deleted_at', null)

  const byId = new Map((obligations ?? []).map((o) => [o.id as string, o]))

  // is_estimate lives on the instalment, not in the derived-state view: the
  // view is about what the ledger says happened, and an estimate is about
  // whether anything is owed at all.
  const { data: instalments } = await supabase
    .from('instalments')
    .select('id, is_estimate, recurring_rule_id')
    .in('id', states.map((s) => s.instalment_id as string))
  const instById = new Map((instalments ?? []).map((i) => [i.id as string, i]))

  const partyIds = [...new Set((obligations ?? []).map((o) => o.party_id).filter(Boolean))] as string[]
  const { data: parties } = partyIds.length
    ? await supabase.from('parties').select('id, name, pinned_ibans').in('id', partyIds)
    : { data: [] as never[] }

  const partyById = new Map((parties ?? []).map((p) => [p.id as string, p]))

  // Which space an item belongs to is the answer to "is this for the
  // restaurant or the house" — the question the admin asks first and the
  // sender is never asked at all.
  const spaceIds = [...new Set(states.map((s) => s.space_id as string))]
  const { data: spaces } = spaceIds.length
    ? await supabase.from('spaces').select('id, name, kind').in('id', spaceIds)
    : { data: [] as never[] }
  const spaceById = new Map((spaces ?? []).map((sp) => [sp.id as string, sp]))

  return states.map((s) => {
    const o = byId.get(s.obligation_id as string)
    const party = o?.party_id ? partyById.get(o.party_id as string) : undefined
    const pinned = ((party?.pinned_ibans ?? []) as { iban: string; occurrences?: number }[])
    const match = pinned.find((p) => p.iban === o?.iban)
    const space = spaceById.get(s.space_id as string)

    return {
      instalmentId: s.instalment_id as string,
      obligationId: s.obligation_id as string,
      spaceId: s.space_id as string,
      description: (o?.description as string) ?? 'Betaling',
      supplierName: (party?.name as string | undefined) ?? null,
      partyId: (o?.party_id as string | null) ?? null,
      amountCents: Number(s.amount_cents),
      paidCents: Number(s.paid_cents ?? 0),
      dueDate: s.due_date as string,
      plannedDate: (s.planned_date as string | null) ?? null,
      state: s.state as PaymentState,
      iban: (o?.iban as string | null) ?? null,
      paymentReference: (o?.payment_reference as string | null) ?? null,
      verified: Boolean(o?.verified_at),
      sourceDocumentId: (o?.source_document_id as string | null) ?? null,
      category: (o?.category as string | null) ?? null,
      spaceName: (space?.name as string | undefined) ?? null,
      spaceKind: (space?.kind as SpaceKind | undefined) ?? null,
      ibanSightings: match?.occurrences ?? 0,
      // Pinned accounts exist but this invoice uses none of them.
      ibanChanged: Boolean(o?.iban) && pinned.length > 0 && !match,
      disputed: Boolean(s.disputed),
      cancelled: Boolean(s.cancelled),
      isHard: Boolean(o?.is_hard),
      isEstimate: Boolean(instById.get(s.instalment_id as string)?.is_estimate),
      recurringRuleId:
        (instById.get(s.instalment_id as string)?.recurring_rule_id as string | null) ?? null,
      notes: (o?.notes as string | null) ?? null,
    }
  })
}

/**
 * Record a payment. Appends to the ledger; never writes a status.
 * Partial amounts are the normal case, not an exception.
 */
export async function markPaid(params: {
  payable: Payable
  amountCents: number
  /** Unused — the database reads auth.uid(). Kept so callers need not change. */
  userId?: string
  note?: string
}): Promise<void> {
  // `userId` is no longer read here: the database takes `auth.uid()`, which is
  // the one version of that answer a browser cannot misstate. It stays in the
  // signature so callers did not all have to change on the same day.
  const { payable, amountCents, note } = params

  // One statement. The event and its log entry are written by `record_payment`
  // inside one transaction (rule 9) — as two round trips, a closed tab between
  // them left money in the ledger with nothing recorded about who put it there.
  const { error } = await supabase.rpc('record_payment', {
    p_instalment_id: payable.instalmentId,
    p_kind: 'payment',
    p_amount_cents: amountCents,
    p_note: note ?? null,
    p_occurred_on: todayIso(),
  })
  if (error) throw error
}

/** When you intend to pay it — distinct from when it is due. */
export async function setPlannedDate(
  payable: Payable,
  date: string | null,
  userId: string,
): Promise<void> {
  const { error } = await supabase
    .from('instalments')
    .update({ planned_date: date })
    .eq('id', payable.instalmentId)
  if (error) throw error

  await supabase.from('activity_log').insert({
    space_id: payable.spaceId,
    actor_id: userId,
    action: 'instalment.planned',
    entity_type: 'instalment',
    entity_id: payable.instalmentId,
    detail: { planned_date: date },
  })
}

export function remainingCents(p: Payable): number {
  return Math.max(0, p.amountCents - p.paidCents)
}

export function stateLabel(state: PaymentState): string {
  return {
    to_pay: 'Te betalen',
    planned: 'Gepland',
    paid: 'Betaald',
    needs_attention: 'Vraagt aandacht',
    cancelled: 'Geannuleerd',
  }[state]
}


/**
 * Everything else that can happen to a payable.
 *
 * `payment_event_kind` has had all of these since the first migration, and the
 * derived-state view already knows what each one means — a dispute makes an
 * instalment need attention, a cancellation takes it out of the list, a credit
 * note counts towards settling it. Until now the app could only ever write
 * 'payment', so five sixths of a working ledger sat unused.
 *
 * Nothing here updates a row. Every one of these appends a fact and lets the
 * view work out what that makes the instalment now (rule 4).
 */
export type LedgerAction =
  | 'dispute_opened'
  | 'dispute_resolved'
  | 'cancelled'
  | 'credit_note'
  | 'write_off'
  | 'refund'

/** What each action means in Dutch, and whether it needs an amount. */
export const LEDGER_ACTIONS: {
  kind: LedgerAction
  label: string
  needsAmount: boolean
  hint: string
}[] = [
  {
    kind: 'dispute_opened',
    label: 'Betwisten',
    needsAmount: false,
    hint: 'Je bent het niet eens met deze factuur. Ze blijft staan, maar wordt gemarkeerd.',
  },
  {
    kind: 'dispute_resolved',
    label: 'Betwisting opgelost',
    needsAmount: false,
    hint: 'Uitgepraat met de leverancier.',
  },
  {
    kind: 'credit_note',
    label: 'Creditnota',
    needsAmount: true,
    hint: 'De leverancier heeft een deel of alles kwijtgescholden.',
  },
  {
    kind: 'refund',
    label: 'Terugbetaling',
    needsAmount: true,
    hint: 'Je hebt geld teruggekregen.',
  },
  {
    kind: 'write_off',
    label: 'Afboeken',
    needsAmount: true,
    hint: 'Dit ga je niet meer betalen en niet meer opvolgen.',
  },
  {
    kind: 'cancelled',
    label: 'Annuleren',
    needsAmount: false,
    hint: 'Deze factuur bestaat niet meer. Ze verdwijnt uit je lijst.',
  },
]

export async function recordLedgerEvent(params: {
  payable: Payable
  kind: LedgerAction
  amountCents?: number
  /** Unused — the database reads auth.uid(). Kept so callers need not change. */
  userId?: string
  note?: string
}): Promise<void> {
  const { payable, kind, amountCents, note } = params

  // Same one-transaction path as markPaid. A dispute, a cancellation or a
  // write-off is a statement about money even when it moves none, and it is
  // worth exactly as much as the record of who made it.
  const { error } = await supabase.rpc('record_payment', {
    p_instalment_id: payable.instalmentId,
    p_kind: kind,
    // Events that are about status rather than money carry zero, not null:
    // the column is not nullable, and a dispute genuinely moves no money.
    p_amount_cents: amountCents ?? 0,
    p_note: note ?? null,
    p_occurred_on: todayIso(),
  })
  if (error) throw error
}

/**
 * Undoing an accidental tick.
 *
 * The ledger is append-only and stays that way: this does not delete the row
 * and does not post a counter-entry. A refund would be a lie — no money came
 * back — and deleting would leave nobody able to explain, a year from now,
 * what happened. The row stays and is marked void; the derived state simply
 * stops counting it (migration 0019).
 *
 * Only a mistake belongs here. Money that genuinely came back is a refund; an
 * invoice that turned out not to be owed is cancelled. Both already exist.
 */
export interface LedgerEntry {
  id: string
  kind: string
  amountCents: number
  occurredOn: string
  note: string | null
}

export async function loadLedger(instalmentId: string): Promise<LedgerEntry[]> {
  const { data, error } = await supabase
    .from('payment_events')
    .select('id, kind, amount_cents, occurred_on, note, voided_at')
    .eq('instalment_id', instalmentId)
    .is('voided_at', null)
    .order('occurred_on', { ascending: false })
  if (error) throw error

  return (data ?? []).map((row) => ({
    id: row.id as string,
    kind: row.kind as string,
    amountCents: Number(row.amount_cents),
    occurredOn: row.occurred_on as string,
    note: (row.note as string | null) ?? null,
  }))
}

export async function undoLedgerEntry(entryId: string, reason?: string): Promise<void> {
  const { error } = await supabase.rpc('void_payment_event', {
    p_event_id: entryId,
    p_reason: reason ?? 'per ongeluk',
  })
  if (error) throw error
}

/** What the ledger row is called on screen. */
export function entryLabel(kind: string): string {
  return (
    {
      payment: 'Betaald',
      dd_confirmed: 'Domiciliëring geïnd',
      refund: 'Terugbetaling',
      credit_note: 'Creditnota',
      write_off: 'Afgeboekt',
      dispute_opened: 'Betwist',
      dispute_resolved: 'Betwisting opgelost',
      cancelled: 'Geannuleerd',
      dd_failed: 'Domiciliëring mislukt',
    }[kind] ?? kind
  )
}
